import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FullPageSpinner } from '@/components/ui/full-page-spinner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { organization } from '@/lib/auth';
import { useAuthState } from '@/lib/auth-state';
import { pruneSearch } from '@/lib/router-search';
import { buildOwnerHref } from '@/lib/subdomain';

/**
 * Create-organization page. `lobu apply` / `lobu org create <slug>` link here
 * with `?slug=&name=` pre-filled; the form mirrors the org-switcher dropdown's
 * "Create organization" action but as a standalone page so a CLI handoff has a
 * URL to send the operator to.
 */
export const Route = createFileRoute('/orgs/new')({
  component: NewOrganizationPage,
  validateSearch: (search: Record<string, unknown>) =>
    pruneSearch({
      slug: (search.slug as string | undefined) || undefined,
      name: (search.name as string | undefined) || undefined,
    }),
});

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function NewOrganizationPage() {
  const navigate = useNavigate();
  const { session, isReady } = useAuthState();
  const { slug: slugParam, name: nameParam } = Route.useSearch();

  const [name, setName] = useState(nameParam || slugParam || '');
  const [slug, setSlug] = useState(slugParam || (nameParam ? slugify(nameParam) : ''));
  const [slugTouched, setSlugTouched] = useState(Boolean(slugParam));
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const redirectingRef = useRef(false);

  // Not signed in → bounce through login, preserving the prefilled params.
  useEffect(() => {
    if (!isReady || session || redirectingRef.current) return;
    redirectingRef.current = true;
    const callback = new URL('/orgs/new', window.location.origin);
    if (slugParam) callback.searchParams.set('slug', slugParam);
    if (nameParam) callback.searchParams.set('name', nameParam);
    const loginUrl = new URL('/auth/login', window.location.origin);
    loginUrl.searchParams.set('callbackUrl', `${callback.pathname}${callback.search}`);
    window.location.assign(loginUrl.toString());
  }, [isReady, session, slugParam, nameParam]);

  if (!isReady || !session) {
    return <FullPageSpinner />;
  }

  const goToOwner = (ownerSlug: string) => {
    const target = buildOwnerHref(ownerSlug);
    if (target.kind === 'cross-host') {
      window.location.assign(target.href);
      return;
    }
    navigate({ to: target.to as '/' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmedName = name.trim();
    const finalSlug = (slug.trim() || slugify(trimmedName)).trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }
    if (!finalSlug) {
      setError('Slug is required');
      return;
    }
    setIsPending(true);
    try {
      const res = await organization.create({ name: trimmedName, slug: finalSlug });
      if (res.error) {
        setError(res.error.message || 'Failed to create organization');
        return;
      }
      if (res.data) {
        await organization.setActive({ organizationId: res.data.id });
        goToOwner(res.data.slug);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div className="container mx-auto max-w-md px-4 py-10">
      <Card>
        <CardHeader>
          <CardTitle>Create organization</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="org-name">
                Name<span className="ml-1 text-destructive">*</span>
              </Label>
              <Input
                id="org-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugTouched) setSlug(slugify(e.target.value));
                }}
                placeholder="Acme Inc."
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-slug">
                Slug<span className="ml-1 text-destructive">*</span>
              </Label>
              <Input
                id="org-slug"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(slugify(e.target.value));
                }}
                placeholder="acme"
              />
              <p className="text-xs text-muted-foreground">
                Used in URLs and as the org id in <code>lobu.toml</code>. Lowercase letters,
                numbers, and hyphens.
              </p>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Creating…' : 'Create organization'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
